import { GitCompare, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { calculatePnl } from "@/utils/pnl";

export const ReconcileModal = ({
  isOpen,
  onClose,
  reconcileData,
  loading,
  exitPrice,
  setExitPrice,
  exitDate,
  setExitDate,
  exitComments,
  setExitComments,
  onCloseFromReconcile,
  isClosing,
}) => {
  // Calculate P&L preview using correct multiplier
  const getPnLPreview = () => {
    if (!exitPrice || !reconcileData?.trade) return null;
    
    const exitPriceNum = parseFloat(exitPrice);
    const trade = reconcileData.trade;
    const { pnlPoints, pnlAmount } = calculatePnl({
      direction: trade.direction,
      entryPrice: trade.entry_price,
      exitPrice: exitPriceNum,
      numLots: trade.num_lots || 1,
      trade,
    });
    
    return { pnlPoints, pnlAmount };
  };

  const pnlPreview = getPnLPreview();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-charcoal border-white/10 text-white max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-purple-400" />
            Reconcile Trade
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Compare journal trade with broker position
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto pr-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin mb-3" />
              <p className="text-slate-400">Fetching broker data...</p>
            </div>
          ) : reconcileData ? (
            <div className="space-y-4 py-4">
              {/* Journal Trade Info */}
              <div className="bg-black/20 p-3 rounded-md">
                <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Journal Trade</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Symbol:</span>{" "}
                    <span className="text-white">{reconcileData.trade.symbol}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Direction:</span>{" "}
                    <span className={reconcileData.trade.direction === "LONG" ? "text-profit" : "text-loss"}>
                      {reconcileData.trade.direction}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Entry:</span>{" "}
                    <span className="text-white">₹{reconcileData.trade.entry_price?.toLocaleString('en-IN')}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Qty:</span>{" "}
                    <span className="text-white">{reconcileData.trade.num_lots} × {reconcileData.trade.lot_size}</span>
                  </div>
                </div>
              </div>

              {/* Broker Position Status */}
              <div className={`p-3 rounded-md border ${reconcileData.position_exists ? 'bg-profit/10 border-profit/30' : 'bg-loss/10 border-loss/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {reconcileData.position_exists ? (
                    <CheckCircle2 className="w-5 h-5 text-profit" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-loss" />
                  )}
                  <p className={`font-medium ${reconcileData.position_exists ? 'text-profit' : 'text-loss'}`}>
                    {reconcileData.position_exists ? 'Position OPEN at Broker' : 'Position CLOSED at Broker'}
                  </p>
                </div>
                
                {reconcileData.broker_position ? (
                  <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                    <div>
                      <span className="text-slate-500">Symbol:</span>{" "}
                      <span className="text-white">{reconcileData.broker_position.tradingsymbol}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Net Qty:</span>{" "}
                      <span className="text-white">{reconcileData.broker_position.netqty}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Avg Price:</span>{" "}
                      <span className="text-white">₹{reconcileData.broker_position.avgnetprice?.toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">LTP:</span>{" "}
                      <span className="text-white">₹{reconcileData.broker_position.ltp?.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">
                    The position has been closed or squared off at the broker.
                  </p>
                )}
              </div>

              {/* Exit Trade Info (if position is closed and we found the exit trade) */}
              {!reconcileData.position_exists && reconcileData.exit_trade_info && (
                <div className="bg-purple-900/20 border border-purple-500/30 p-3 rounded-md">
                  <p className="text-purple-400 text-xs uppercase tracking-wider mb-2">Exit Trade Found</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Exit Price:</span>{" "}
                      <span className="text-white font-mono">₹{reconcileData.exit_trade_info.price?.toLocaleString('en-IN')}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Quantity:</span>{" "}
                      <span className="text-white">{reconcileData.exit_trade_info.quantity}</span>
                    </div>
                    {reconcileData.exit_trade_info.time && (
                      <div className="col-span-2">
                        <span className="text-slate-500">Time:</span>{" "}
                        <span className="text-white">{reconcileData.exit_trade_info.time}</span>
                      </div>
                    )}
                    {reconcileData.exit_trade_info.order_id && (
                      <div className="col-span-2">
                        <span className="text-slate-500">Order ID:</span>{" "}
                        <span className="text-white font-mono text-xs">{reconcileData.exit_trade_info.order_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Current Market Price & Unrealized P&L (only for open positions) */}
              {reconcileData.position_exists && reconcileData.current_price && (
                <div className="bg-blue-900/20 border border-blue-500/30 p-3 rounded-md">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Current Market Price:</span>
                    <span className="text-white font-mono">
                      ₹{reconcileData.current_price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  {reconcileData.unrealized_pnl && (
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-slate-400">Unrealized P&L:</span>
                      <span className={`font-mono font-bold ${reconcileData.unrealized_pnl.pnl_amount >= 0 ? 'text-profit' : 'text-loss'}`}>
                        ₹{reconcileData.unrealized_pnl.pnl_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        <span className="text-sm font-normal ml-1">({reconcileData.unrealized_pnl.pnl_points} pts)</span>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Close Trade Section (if position is closed at broker) */}
              {!reconcileData.position_exists && (
                <div className="border border-loss/30 rounded-md p-4">
                  <h4 className="text-loss font-medium mb-3">Close This Trade</h4>
                  <p className="text-slate-400 text-sm mb-3">
                    {reconcileData.actual_exit_price 
                      ? "Position closed at broker. Exit price pre-filled from trade book. Review and confirm:"
                      : "Position closed at broker. Enter exit details to close this journal trade:"}
                  </p>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-300 text-sm">Exit Price *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={exitPrice}
                          onChange={(e) => setExitPrice(e.target.value)}
                          className="bg-black/20 border-white/10 text-white mt-1"
                          placeholder="Enter exit price"
                          data-testid="reconcile-exit-price"
                        />
                        {reconcileData.actual_exit_price && (
                          <p className="text-xs text-purple-400 mt-1">From trade book</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-slate-300 text-sm">Exit Date *</Label>
                        <Input
                          type="datetime-local"
                          value={exitDate}
                          onChange={(e) => setExitDate(e.target.value)}
                          className="bg-black/20 border-white/10 text-white mt-1"
                          data-testid="reconcile-exit-date"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-300 text-sm">Exit Comments</Label>
                      <Textarea
                        value={exitComments}
                        onChange={(e) => setExitComments(e.target.value)}
                        className="bg-black/20 border-white/10 text-white mt-1"
                        placeholder="Optional: Add exit notes..."
                        rows={2}
                        data-testid="reconcile-exit-comments"
                      />
                    </div>

                    {/* P&L Preview */}
                    {pnlPreview && (
                      <div className="bg-black/30 p-3 rounded">
                        <p className="text-slate-400 text-sm mb-1">Estimated P&L:</p>
                        <p className={`text-2xl font-bold ${pnlPreview.pnlAmount >= 0 ? 'text-profit' : 'text-loss'}`}>
                          ₹{pnlPreview.pnlAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          <span className="text-sm font-normal ml-2">({pnlPreview.pnlPoints.toFixed(2)} pts)</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 pt-4 border-t border-white/5">
          <Button 
            variant="outline" 
            onClick={() => onClose(false)} 
            className="border-white/10 text-slate-300"
          >
            Close
          </Button>
          {reconcileData && !reconcileData.position_exists && (
            <Button 
              onClick={onCloseFromReconcile} 
              disabled={isClosing || !exitPrice || !exitDate}
              className="bg-loss hover:bg-loss/80 text-white"
              data-testid="reconcile-close-trade-btn"
            >
              {isClosing ? "Closing..." : "Close Trade"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReconcileModal;
